import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { modelService } from '../services/model-service';
import {
  PricingModel,
  CreatePricingModelData,
  CreateFromTemplateData,
  UpdatePricingModelData,
  CreateVariableData,
  UpdateVariableData,
  Variable,
  LookupTable,
  CreateLookupTableData,
  UpdateLookupTableData,
} from '../types/model.types';

/**
 * Estado de modelos de pricing
 */
interface ModelState {
  // Estado
  models: PricingModel[];
  currentModel: PricingModel | null;
  variables: Variable[];
  lookupTables: LookupTable[];
  isLoading: boolean;
  error: string | null;

  // Acciones - Models
  fetchModels: () => Promise<void>;
  fetchModel: (id: string) => Promise<void>;
  createModel: (data: CreatePricingModelData) => Promise<PricingModel>;
  createFromTemplate: (data: CreateFromTemplateData) => Promise<PricingModel>;
  updateModel: (id: string, data: UpdatePricingModelData) => Promise<PricingModel>;
  deleteModel: (id: string) => Promise<void>;
  cloneModel: (id: string) => Promise<PricingModel>;
  setCurrentModel: (model: PricingModel | null) => void;

  // Acciones - Variables
  fetchVariables: (modelId: string) => Promise<void>;
  createVariable: (modelId: string, data: CreateVariableData) => Promise<Variable>;
  updateVariable: (
    modelId: string,
    variableId: string,
    data: UpdateVariableData
  ) => Promise<Variable>;
  deleteVariable: (modelId: string, variableId: string) => Promise<void>;
  reorderVariables: (modelId: string, variableIds: string[]) => Promise<void>;

  // Acciones - Lookup Tables
  fetchLookupTables: (modelId: string) => Promise<void>;
  createLookupTable: (modelId: string, data: CreateLookupTableData) => Promise<LookupTable>;
  updateLookupTable: (
    modelId: string,
    tableId: string,
    data: UpdateLookupTableData
  ) => Promise<LookupTable>;
  deleteLookupTable: (modelId: string, tableId: string) => Promise<void>;

  // Helpers
  clearError: () => void;
}

export const useModelStore = create<ModelState>()(
  devtools(
    (set, get) => ({
      // Estado inicial
      models: [],
      currentModel: null,
      variables: [],
      lookupTables: [],
      isLoading: false,
      error: null,

      // ============ Models ============
      fetchModels: async () => {
        try {
          set({ isLoading: true, error: null });
          const models = await modelService.getModels();
          set({ models, isLoading: false });
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al cargar modelos',
            isLoading: false,
          });
          throw error;
        }
      },

      fetchModel: async (id) => {
        try {
          set({ isLoading: true, error: null });
          const model = await modelService.getModel(id);
          set({
            currentModel: model,
            lookupTables: model.lookupTables || [],
            isLoading: false,
          });
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al cargar modelo',
            isLoading: false,
          });
          throw error;
        }
      },

      createModel: async (data) => {
        try {
          set({ isLoading: true, error: null });
          const model = await modelService.createModel(data);
          set((state) => ({
            models: [...state.models, model],
            isLoading: false,
          }));
          return model;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al crear modelo',
            isLoading: false,
          });
          throw error;
        }
      },

      createFromTemplate: async (data) => {
        try {
          set({ isLoading: true, error: null });
          const model = await modelService.createFromTemplate(data);
          set((state) => ({
            models: [...state.models, model],
            isLoading: false,
          }));
          return model;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al crear modelo desde template',
            isLoading: false,
          });
          throw error;
        }
      },

      updateModel: async (id, data) => {
        try {
          set({ isLoading: true, error: null });
          const model = await modelService.updateModel(id, data);
          set((state) => ({
            models: state.models.map((m) => (m.id === id ? model : m)),
            currentModel: state.currentModel?.id === id ? model : state.currentModel,
            isLoading: false,
          }));
          return model;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al actualizar modelo',
            isLoading: false,
          });
          throw error;
        }
      },

      deleteModel: async (id) => {
        try {
          set({ isLoading: true, error: null });
          await modelService.deleteModel(id);
          set((state) => ({
            models: state.models.filter((m) => m.id !== id),
            currentModel: state.currentModel?.id === id ? null : state.currentModel,
            isLoading: false,
          }));
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al eliminar modelo',
            isLoading: false,
          });
          throw error;
        }
      },

      cloneModel: async (id) => {
        try {
          set({ isLoading: true, error: null });
          const model = await modelService.cloneModel(id);
          set((state) => ({
            models: [...state.models, model],
            isLoading: false,
          }));
          return model;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al clonar modelo',
            isLoading: false,
          });
          throw error;
        }
      },

      setCurrentModel: (model) => {
        set({ currentModel: model });
      },

      // ============ Variables ============
      fetchVariables: async (modelId) => {
        try {
          set({ isLoading: true, error: null });
          const variables = await modelService.getVariables(modelId);
          set({ variables, isLoading: false });
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al cargar variables',
            isLoading: false,
          });
          throw error;
        }
      },

      createVariable: async (modelId, data) => {
        try {
          set({ isLoading: true, error: null });
          const variable = await modelService.createVariable(modelId, data);
          set((state) => ({
            variables: [...state.variables, variable],
            isLoading: false,
          }));
          return variable;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al crear variable',
            isLoading: false,
          });
          throw error;
        }
      },

      updateVariable: async (modelId, variableId, data) => {
        try {
          set({ isLoading: true, error: null });
          const variable = await modelService.updateVariable(modelId, variableId, data);
          set((state) => ({
            variables: state.variables.map((v) => (v.id === variableId ? variable : v)),
            isLoading: false,
          }));
          return variable;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al actualizar variable',
            isLoading: false,
          });
          throw error;
        }
      },

      deleteVariable: async (modelId, variableId) => {
        try {
          set({ isLoading: true, error: null });
          await modelService.deleteVariable(modelId, variableId);
          set((state) => ({
            variables: state.variables.filter((v) => v.id !== variableId),
            isLoading: false,
          }));
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al eliminar variable',
            isLoading: false,
          });
          throw error;
        }
      },

      reorderVariables: async (modelId, variableIds) => {
        try {
          set({ isLoading: true, error: null });
          await modelService.reorderVariables(modelId, variableIds);
          // Re-fetch variables to get updated order
          await get().fetchVariables(modelId);
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al reordenar variables',
            isLoading: false,
          });
          throw error;
        }
      },

      // ============ Lookup Tables ============
      fetchLookupTables: async (modelId) => {
        try {
          set({ isLoading: true, error: null });
          const lookupTables = await modelService.getLookupTables(modelId);
          set({ lookupTables, isLoading: false });
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al cargar tablas de lookup',
            isLoading: false,
          });
          throw error;
        }
      },

      createLookupTable: async (modelId, data) => {
        try {
          set({ isLoading: true, error: null });
          const table = await modelService.createLookupTable(modelId, data);
          set((state) => ({
            lookupTables: [...state.lookupTables, table],
            isLoading: false,
          }));
          return table;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al crear tabla de lookup',
            isLoading: false,
          });
          throw error;
        }
      },

      updateLookupTable: async (modelId, tableId, data) => {
        try {
          set({ isLoading: true, error: null });
          const table = await modelService.updateLookupTable(modelId, tableId, data);
          set((state) => ({
            lookupTables: state.lookupTables.map((t) => (t.id === tableId ? table : t)),
            isLoading: false,
          }));
          return table;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al actualizar tabla de lookup',
            isLoading: false,
          });
          throw error;
        }
      },

      deleteLookupTable: async (modelId, tableId) => {
        try {
          set({ isLoading: true, error: null });
          await modelService.deleteLookupTable(modelId, tableId);
          set((state) => ({
            lookupTables: state.lookupTables.filter((t) => t.id !== tableId),
            isLoading: false,
          }));
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al eliminar tabla de lookup',
            isLoading: false,
          });
          throw error;
        }
      },

      // ============ Helpers ============
      clearError: () => {
        set({ error: null });
      },
    }),
    { name: 'ModelStore' }
  )
);

export default useModelStore;
